import React from 'react';
import styled from 'styled-components';
import { FiCalendar, FiMap, FiCompass, FiBriefcase, FiPlus, FiMapPin, FiCheckCircle } from 'react-icons/fi';
import { colors } from '../theme';
import { isRestaurant } from '../services/restaurantService';

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Hero = styled.div`
  text-align: center;
  padding: 40px 20px 35px;
  margin-bottom: 24px;
  border-radius: 12px;
  background: linear-gradient(135deg, #E31B23, #006E33, #FFD700);
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
  background-image: url('https://images.pexels.com/photos/27091001/pexels-photo-27091001/free-photo-of-luis-i-bridge.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2');
  background-size: cover;
  background-position: center;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  font-weight: 800;
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  max-width: 800px;
  margin: 0 auto 40px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
`;

const Title = styled.h1`
  font-size: 3.5rem;
  color: white;
  margin-bottom: 1.2rem;
  font-family: 'Playfair Display', serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.5px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.95);
  max-width: 800px;
  margin: 0 auto 3rem;
  font-weight: 500;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }
`;

const Section = styled.section`
  margin-bottom: 64px;
  
  @media (max-width: 768px) {
    margin-bottom: 48px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: ${colors.primary};
  margin-bottom: 1.8rem;
  position: relative;
  display: inline-block;
  font-weight: 800;
  letter-spacing: -0.5px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 28px;
  margin: 32px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const TemplateCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px 25px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(.4,0,.2,1);
  border: 2px solid #f0f0f0;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);
    border-color: #e0e0e0;
  }
  
  @media (max-width: 768px) {
    padding: 28px 20px;
  }
`;

const TemplateIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #E31B23, #FFD700);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
  font-size: 2rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transition: all 0.4s cubic-bezier(.4,0,.2,1);
  
  ${TemplateCard}:hover & {
    transform: scale(1.15) rotate(8deg);
    background: linear-gradient(135deg, #006E33, #FFD700);
  }
  
  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    font-size: 1.8rem;
    margin-bottom: 16px;
  }
`;

const TemplateTitle = styled.h3`
  font-size: 1.4rem;
  margin: 0 0 12px;
  color: ${colors.text};
  font-weight: 700;
  letter-spacing: -0.3px;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 8px;
  }
`;

const TemplateDescription = styled.p`
  color: ${colors.textSecondary};
  font-size: 1rem;
  margin-bottom: 20px;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 16px;
  }
`;

const GuideItem = styled.div`
  background: linear-gradient(to right, #FFFFFF, #F8F8F8);
  border-radius: 18px;
  padding: 28px 32px;
  margin-bottom: 24px;
  border-left: 6px solid #E31B23;
  display: flex;
  align-items: flex-start;
  gap: 22px;
  transition: all 0.3s cubic-bezier(.4,0,.2,1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  
  &:hover {
    transform: translateX(8px) scale(1.02);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
    border-left-color: #006E33;
  }
  
  &:nth-child(2) {
    border-left-color: #FFD700;
  }
  
  &:nth-child(3) {
    border-left-color: #006E33;
  }
  
  @media (max-width: 768px) {
    padding: 24px;
    gap: 16px;
    margin-bottom: 20px;
  }
`;

const GuideNumber = styled.div`
  background: linear-gradient(135deg, #E31B23, #FFD700);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: bold;
  font-size: 1.2rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(.4,0,.2,1);
  
  ${GuideItem}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
  
  ${GuideItem}:nth-child(2) & {
    background: linear-gradient(135deg, #FFD700, #E31B23);
  }
  
  ${GuideItem}:nth-child(3) & {
    background: linear-gradient(135deg, #006E33, #FFD700);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
`;

const GuideContent = styled.div`
  flex: 1;
`;

const GuideTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #2C3E50;
  font-size: 1.2rem;
  font-weight: 600;
  font-family: 'Playfair Display', serif;
`;

const GuideText = styled.p`
  margin: 0;
  color: #5D6D7E;
  font-size: 1rem;
  line-height: 1.6;
`;

const SectionTitleWrapper = styled.div`
  position: relative;
  margin-bottom: 2rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #FF0000, #FFCC00, #006600);
    border-radius: 2px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 36px;
  position: relative;
  z-index: 1;
`;

const SummaryCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 22px 18px;
  border: 1px solid #e6e9ef;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.2, 0.9, 0.4, 1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3a7bd5, #00d2ff);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(63, 138, 224, 0.15);
    border-color: #d0e0ff;
  }
  
  @media (max-width: 768px) {
    padding: 18px 12px;
  }
`;

const SummaryIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f8f9ff, #f0f4ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #3a7bd5;
  font-size: 1.4rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.12);
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0));
    top: 0;
    left: 0;
  }
  
  ${SummaryCard}:hover & {
    transform: rotate(8deg) scale(1.1);
    background: linear-gradient(135deg, #3a7bd5, #00d2ff);
    color: white;
    box-shadow: 0 6px 16px rgba(58, 123, 213, 0.2);
  }
`;

const SummaryTitle = styled.h3`
  font-size: 1.15rem;
  color: #2d3748;
  margin: 0 0 10px 0;
  font-weight: 650;
  letter-spacing: -0.3px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
`;

const SummaryValue = styled.div`
  font-size: 2rem;
  font-weight: 750;
  color: #1a365d;
  margin: 4px 0 6px 0;
  background: linear-gradient(90deg, #3a7bd5, #00d2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
`;

const SummaryLabel = styled.span`
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 12px 0;
  line-height: 1.4;
  max-width: 90%;
  opacity: 0.9;
`;

const ChecklistPill = styled.li`
  display: inline-block;
  background: ${({ checked }) => (checked ? 'linear-gradient(90deg,#DFF5E3,#B7EACB)' : '#f6dada')};
  color: ${({ checked }) => (checked ? '#006E33' : '#E31B23')};
  border-radius: 12px;
  padding: 4px 13px;
  font-size: 0.93em;
  margin: 0 7px 7px 0;
  font-weight: 500;
  box-shadow: ${({ checked }) => (checked ? '0 1px 4px #b7eacb50' : '0 1px 4px #eecaca50')};
`;

const LocalExperienceCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  &:hover {
    transform: translateY(-5px);
  }
`;

const ExperienceImage = styled.div`
  height: 180px;
  background-size: cover;
  background-position: center;
`;

const ExperienceContent = styled.div`
  padding: 20px;
`;

const QuoteSection = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  margin: 40px 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
`;

const QuoteText = styled.p`
  font-size: 1.5rem;
  font-style: italic;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto 10px;
`;

const QuoteAuthor = styled.p`
  font-size: 1rem;
  color: #E31B23;
  font-weight: bold;
`;

const HomeTab = ({ onSelectTemplate, setActiveTab, days = [], activitiesByCity = {}, profile, checklistProgress = 0 }) => {
  const templates = [
    // Regular Templates
    {
      id: '3days',
      title: 'Weekend Getaway',
      description: 'Perfect for a short escape to Portugal',
      icon: <FiCalendar />,
      days: 3,
      color: '#E31B23',  // Portuguese Red
      isPrivateTour: false
    },
    {
      id: '7days',
      title: 'One Week in Portugal',
      description: 'Explore multiple cities at a relaxed pace',
      icon: <FiMap />,
      days: 7,
      color: '#006E33',  // Portuguese Green
      isPrivateTour: false
    },
    {
      id: '14days',
      title: '2-Week Adventure',
      description: 'Immerse yourself in Portuguese culture',
      icon: <FiCompass />,
      days: 14,
      color: '#FFD700',  // Gold
      textColor: '#333',
      isPrivateTour: false
    },
    {
      id: 'custom',
      title: 'Custom Trip',
      description: 'Create your own personalized itinerary',
      icon: <FiPlus />,
      days: 1,
      color: '#5D3A9B',  // Deep Purple
      isPrivateTour: false
    }
  ];

  const guides = [
    {
      number: 1,
      title: 'Plan Your Route',
      text: 'Start by adding destinations and activities to your itinerary.'
    },
    {
      number: 2,
      title: 'Customize Your Trip',
      text: 'Drag and drop to rearrange activities and optimize your schedule.'
    },
    {
      number: 3,
      title: 'Save & Share',
      text: 'Save your progress and share your itinerary with travel companions.'
    }
  ];

  // Dates
  const totalDays = days.length;
  const startDate = profile?.startDate ? new Date(profile.startDate) : null;
  const endDate = startDate ? new Date(startDate) : null;
  if (endDate) endDate.setDate(endDate.getDate() + totalDays - 1);

  // Use the checklistProgress prop from parent component

  // Restaurants needing reservation
  const restaurantsToReserve = days.flatMap(day => Object.values(day.slots || {})).flat().filter(item => isRestaurant(item) && !item.reservation).map(item => item.title);

  // Days with at least one free slot
  const daysWithFreeSlots = days.map((day, i) => ({
    i,
    label: day.label || `Day ${i + 1}`,
    city: day.city,
    freeSlots: Object.entries(day.slots || {}).filter(([slot, items]) => items.length === 0).map(([slot]) => slot)
  })).filter(day => day.freeSlots.length > 0);

  // Days overbooked (all slots filled)
  const daysOverbooked = days.map((day, i) => ({
    i,
    label: day.label || `Day ${i + 1}`,
    city: day.city,
    allFilled: Object.values(day.slots || {}).every(items => items.length > 0)
  })).filter(day => day.allFilled);

  // Must-see sights not scheduled (if activitiesByCity is provided)
  let unscheduledMustSee = [];
  if (activitiesByCity && days.length) {
    const scheduledTitles = new Set(days.flatMap(day => Object.values(day.slots || {})).flat().map(item => item.title));
    unscheduledMustSee = Object.values(activitiesByCity).flat().filter(a => a.mustSee && !scheduledTitles.has(a.title));
  }

  const WelcomeMessage = styled.div`
  background: linear-gradient(135deg, #006E33, #FFD700);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  margin-bottom: 32px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 20px;
  }
`;

const ActionButton = styled.button`
  background: white;
  color: #006E33;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

  const quotes = [
    { text: "Portugal is not a small country.", author: "Álvaro de Campos (Fernando Pessoa)" },
    { text: "One cannot know Portugal without knowing its sea.", author: "Portuguese Proverb" },
    { text: "Through uncharted seas we plowed.", author: "Luís de Camões" },
    { text: "Portugal, light of the Atlantic, cradle of discoveries.", author: "Anonymous" }
  ];

  const [currentQuote, setCurrentQuote] = React.useState(
    quotes[Math.floor(Math.random() * quotes.length)]
  );

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Container>
      <Hero>
        <HeroTitle>Discover the Magic of Portugal</HeroTitle>
        <HeroSubtitle>
          Explore the rich history, stunning landscapes, and vibrant culture of Portugal with our expertly curated itineraries.
        </HeroSubtitle>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <ActionButton 
            onClick={() => setActiveTab('planner')} 
            style={{ 
              background: '#E31B23',
              color: 'white',
              padding: '16px 32px',
              fontSize: '1.1rem'
            }}
          >
            Start Planning
          </ActionButton>
          <ActionButton 
            onClick={() => setActiveTab('explore')} 
            style={{ 
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              padding: '16px 32px',
              fontSize: '1.1rem',
              backdropFilter: 'blur(10px)'
            }}
          >
            Explore Destinations
          </ActionButton>
        </div>
      </Hero>

      <QuoteSection>
        <QuoteText>"{currentQuote.text}"</QuoteText>
        <QuoteAuthor>- {currentQuote.author}</QuoteAuthor>
      </QuoteSection>

      <Section>
        <SectionTitleWrapper>
          <SectionTitle>Trip Summary</SectionTitle>
        </SectionTitleWrapper>
        <SummaryGrid>
          <SummaryCard 
            style={{ 
              background: checklistProgress === 100 ? '#f0fff0' : 
                       checklistProgress > 50 ? '#fff8e0' : '#fff0f0' 
            }}
            onClick={() => setActiveTab('essentials')}
          >
            <SummaryIconWrap style={{ 
              background: checklistProgress === 100 ? '#4CAF50' : 
                       checklistProgress > 50 ? '#FFC107' : '#FF5722' 
            }}>
              <FiCheckCircle />
            </SummaryIconWrap>
            <SummaryValue>{checklistProgress}%</SummaryValue>
            <SummaryTitle>Preparedness</SummaryTitle>
            <SummaryLabel>Travel checklist completion</SummaryLabel>
            {checklistProgress < 100 && (
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                {checklistProgress === 0 ? 'Start your checklist' : 'Continue checklist'}
              </div>
            )}
          </SummaryCard>
          <SummaryCard style={{ background: restaurantsToReserve.length ? '#fff8f0' : undefined }}>
            <SummaryIconWrap style={{ background: restaurantsToReserve.length ? 'linear-gradient(135deg,#E31B23,#FFD700)' : undefined }}>
              <FiMapPin />
            </SummaryIconWrap>
            <h3 style={{ fontSize: '1.13rem', margin: '0 0 7px 0', fontWeight: 700, letterSpacing: 0.2 }}>Restaurants Needing Reservation</h3>
            <p style={{ color: restaurantsToReserve.length ? '#E31B23' : '#006E33', fontWeight: 600, fontSize: '1.18em', margin: 0 }}>{restaurantsToReserve.length}</p>
            {restaurantsToReserve.length > 0 && (
              <ul style={{ fontSize: '0.98em', margin: '7px 0 0 0', padding: 0, listStyle: 'none', textAlign: 'center' }}>
                {restaurantsToReserve.slice(0, 3).map((title, idx) => (
                  <li key={title}>{title}</li>
                ))}
                {restaurantsToReserve.length > 3 && <li>...and more</li>}
              </ul>
            )}
          </SummaryCard>
          <SummaryCard style={{ background: daysWithFreeSlots.length ? '#f0fff8' : undefined }}>
            <SummaryIconWrap style={{ background: daysWithFreeSlots.length ? 'linear-gradient(135deg,#006E33,#FFD700)' : undefined }}>
              <FiMap />
            </SummaryIconWrap>
            <h3 style={{ fontSize: '1.13rem', margin: '0 0 7px 0', fontWeight: 700, letterSpacing: 0.2 }}>Days with Free Time</h3>
            <p style={{ color: '#006E33', fontWeight: 600, fontSize: '1.18em', margin: 0 }}>{daysWithFreeSlots.length}</p>
            {daysWithFreeSlots.length > 0 && (
              <ul style={{ fontSize: '0.98em', margin: '7px 0 0 0', padding: 0, listStyle: 'none', textAlign: 'center' }}>
                {daysWithFreeSlots.slice(0, 2).map(day => (
                  <li key={day.label}>{day.label} ({day.freeSlots.join(', ')})</li>
                ))}
                {daysWithFreeSlots.length > 2 && <li>...and more</li>}
              </ul>
            )}
          </SummaryCard>
          <SummaryCard style={{ background: daysOverbooked.length ? '#fff0f0' : undefined }}>
            <SummaryIconWrap style={{ background: daysOverbooked.length ? 'linear-gradient(135deg,#E31B23,#FFD700)' : undefined }}>
              <FiBriefcase />
            </SummaryIconWrap>
            <h3 style={{ fontSize: '1.13rem', margin: '0 0 7px 0', fontWeight: 700, letterSpacing: 0.2 }}>Days Fully Booked</h3>
            <p style={{ color: daysOverbooked.length ? '#E31B23' : '#444', fontWeight: 600, fontSize: '1.18em', margin: 0 }}>{daysOverbooked.length}</p>
            {daysOverbooked.length > 0 && (
              <ul style={{ fontSize: '0.98em', margin: '7px 0 0 0', padding: 0, listStyle: 'none', textAlign: 'center' }}>
                {daysOverbooked.slice(0, 2).map(day => (
                  <li key={day.label}>{day.label} ({day.city})</li>
                ))}
                {daysOverbooked.length > 2 && <li>...and more</li>}
              </ul>
            )}
          </SummaryCard>
          <SummaryCard style={{ background: unscheduledMustSee.length ? '#fffbe0' : undefined }}>
            <SummaryIconWrap style={{ background: unscheduledMustSee.length ? 'linear-gradient(135deg,#FFD700,#E31B23)' : undefined }}>
              <FiPlus />
            </SummaryIconWrap>
            <h3 style={{ fontSize: '1.13rem', margin: '0 0 7px 0', fontWeight: 700, letterSpacing: 0.2 }}>Must-See Unscheduled</h3>
            <p style={{ color: unscheduledMustSee.length ? '#FFD700' : '#444', fontWeight: 600, fontSize: '1.18em', margin: 0 }}>{unscheduledMustSee.length}</p>
            {unscheduledMustSee.length > 0 && (
              <ul style={{ fontSize: '0.98em', margin: '7px 0 0 0', padding: 0, listStyle: 'none', textAlign: 'center' }}>
                {unscheduledMustSee.slice(0, 3).map(a => (
                  <li key={a.title}>{a.title} ({a.city || ''})</li>
                ))}
                {unscheduledMustSee.length > 3 && <li>...and more</li>}
              </ul>
            )}
          </SummaryCard>
        </SummaryGrid>
      </Section>
    </Container>
  );
};

export default HomeTab;
